import { useEffect, useRef, useState } from 'react';
import {
  CARD_CANVAS_H,
  CARD_CANVAS_W,
  CARD_FRAME_CENTER_X,
  CARD_FRAME_CENTER_Y,
  CARD_FRAME_H,
  CARD_FRAME_W,
  type FaceFit,
} from '@underline/shared';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

const MIN_SCALE = 0.2;
const MAX_SCALE = 5;
const DISPLAY_W = 320; // largura do preview em CSS px — a matemática é resolution-independent

interface FaceFitEditorProps {
  open: boolean;
  /** URL do retrato já convertido no servidor (não um blob local). */
  portraitUrl: string | null;
  imgW: number | null;
  imgH: number | null;
  /** Encaixe já salvo, pra reabrir o editor sobre uma foto existente. */
  initialFaceFit?: FaceFit | null;
  onClose: () => void;
  onConfirm: (faceFit: FaceFit) => void;
  isSaving?: boolean;
}

function defaultFit(imgW: number, imgH: number): { scale: number; rotate: number; x: number; y: number } {
  // Cover-fit inicial: a imagem cobre o canvas inteiro, centralizada.
  const scale = Math.max(CARD_CANVAS_W / imgW, CARD_CANVAS_H / imgH);
  return { scale, rotate: 0, x: 0, y: 0 };
}

export function FaceFitEditor({
  open,
  portraitUrl,
  imgW,
  imgH,
  initialFaceFit,
  onClose,
  onConfirm,
  isSaving,
}: FaceFitEditorProps) {
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [pos, setPos] = useState({ x: 0, y: 0 }); // px no espaço do canvas 1080x1920
  const dragRef = useRef<{ startX: number; startY: number; posX: number; posY: number } | null>(
    null,
  );

  useEffect(() => {
    if (!open || !imgW || !imgH) return;
    if (initialFaceFit) {
      setScale(initialFaceFit.scale);
      setRotate(initialFaceFit.rotate);
      setPos({ x: initialFaceFit.x, y: initialFaceFit.y });
    } else {
      const d = defaultFit(imgW, imgH);
      setScale(d.scale);
      setRotate(d.rotate);
      setPos({ x: d.x, y: d.y });
    }
  }, [open, imgW, imgH, initialFaceFit]);

  if (!imgW || !imgH || !portraitUrl) return null;

  const previewScale = DISPLAY_W / CARD_CANVAS_W;
  const displayH = CARD_CANVAS_H * previewScale;

  function toCanvasDelta(dxScreen: number, dyScreen: number) {
    return { dx: dxScreen / previewScale, dy: dyScreen / previewScale };
  }

  function handlePointerDown(e: React.PointerEvent) {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, posX: pos.x, posY: pos.y };
  }

  function handlePointerMove(e: React.PointerEvent) {
    e.stopPropagation();
    if (!dragRef.current) return;
    const { dx, dy } = toCanvasDelta(
      e.clientX - dragRef.current.startX,
      e.clientY - dragRef.current.startY,
    );
    setPos({ x: dragRef.current.posX + dx, y: dragRef.current.posY + dy });
  }

  function handlePointerUp(e: React.PointerEvent) {
    e.stopPropagation();
    dragRef.current = null;
  }

  function handleConfirm() {
    if (!imgW || !imgH) return;
    onConfirm({ img_w: imgW, img_h: imgH, scale, rotate, x: pos.x, y: pos.y });
  }

  const imgDisplayW = imgW * previewScale;
  const imgDisplayH = imgH * previewScale;

  const frameLeft = (CARD_FRAME_CENTER_X - CARD_FRAME_W / 2) * previewScale;
  const frameTop = (CARD_FRAME_CENTER_Y - CARD_FRAME_H / 2) * previewScale;
  const frameW = CARD_FRAME_W * previewScale;
  const frameH = CARD_FRAME_H * previewScale;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="z-[70] fixed inset-0 top-0 left-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0 rounded-none sm:rounded-none border-0 p-0 flex flex-col bg-background">
        <DialogTitle className="sr-only">Encaixar rosto do DJ na moldura</DialogTitle>

        <div className="flex items-center justify-between px-5 pt-[calc(env(safe-area-inset-top)+16px)] pb-3">
          <h2 className="u-h3">Encaixar rosto na moldura</h2>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 overflow-hidden">
          <div
            className="relative overflow-hidden rounded-2xl bg-surface-2 touch-none select-none"
            style={{ width: DISPLAY_W, height: displayH }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          >
            <img
              src={portraitUrl}
              alt=""
              draggable={false}
              className="absolute left-1/2 top-1/2 origin-center"
              style={{
                width: imgDisplayW,
                height: imgDisplayH,
                transform: `translate(-50%, -50%) translate(${pos.x * previewScale}px, ${pos.y * previewScale}px) rotate(${rotate}deg) scale(${scale})`,
              }}
            />
            <img
              src="/moldura-rosto.svg"
              alt=""
              draggable={false}
              className="pointer-events-none absolute object-contain opacity-40 mix-blend-multiply"
              style={{
                left: frameLeft,
                top: frameTop,
                width: frameW,
                height: frameH,
                filter: 'invert(13%) sepia(90%) saturate(6000%) hue-rotate(350deg)',
              }}
            />
          </div>
        </div>

        <div className="px-6 py-4 space-y-3">
          <div className="flex items-center gap-3">
            <span className="mono text-xs uppercase text-muted-foreground w-14 shrink-0">Zoom</span>
            <input
              type="range"
              min={MIN_SCALE}
              max={MAX_SCALE}
              step={0.01}
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="flex-1"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="mono text-xs uppercase text-muted-foreground w-14 shrink-0">Girar</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={rotate}
              onChange={(e) => setRotate(Number(e.target.value))}
              className="flex-1"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium hover:bg-muted"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isSaving}
              className="flex-[2] rounded-lg bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-red disabled:opacity-60"
            >
              {isSaving ? 'Salvando...' : 'Usar foto'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
